'use client';

import { FC, useMemo } from 'react';
import { NoResults } from '@bts-web/core-features/common';
import { TTransfersNextStepStateWithActions } from '../../utils/useTransfersNextStepState.client';
import { TCryptoTransferTranslations } from '../../utils/getCryptoTransferTranslations';
import { TransfersActionBar } from '../Reusable/ActionBar/TransfersActionBar';
import { AssetPickerList } from '../Reusable/AssetPickerList/AssetPickerList';
import { NetworkPickerList } from '../Reusable/NetworkPickerList/NetworkPickerList';
import { AssetAndNetworkPage } from '../Reusable/AssetAndNetworkPage/AssetAndNetworkPage';
import { TransfersLayout } from '../Reusable/TransfersLayout/TransfersLayout';
import { DepositWalletPage } from './components/DepositWalletPage/DepositWalletPage';
import { useTransfersAssetsListContext } from '../TransfersAssetListContext/TransfersAssetListContext';
import { AssetsListSkeleton } from '../TransfersParent/TransfersSkeleton';
import {
  TransfersCommonStepsIds,
  TransfersDepositStepsIds,
  TransfersSectionVariants,
} from '../../types';
import { CryptoTransfersSearchBar } from '../CryptoTransfersSearchBar/CryptoTransfersSearchBar';
import { useClientLocale } from '@bts-web/utils-lokalise';

export type DepositScreenProps = {
  state: TTransfersNextStepStateWithActions;
  transfersTranslations: TCryptoTransferTranslations;
};

const DepositScreen: FC<DepositScreenProps> = ({
  state,
  transfersTranslations,
}) => {
  const locale = useClientLocale();

  const { assetsListForDeposit, isAssetsListLoading, assetFilterSearchValue } =
    useTransfersAssetsListContext();

  const { pageId, selectedAssetData, bindAssetData, bindNetworkData } = state;

  const currentNetworkList = useMemo(() => {
    if (!selectedAssetData) return null;

    const networkList = assetsListForDeposit.find(
      (item) => item.id === selectedAssetData?.id,
    )?.networkInformation?.networks;

    return networkList ?? null;
  }, [assetsListForDeposit, selectedAssetData]);

  const hasMultipleNetworks = currentNetworkList
    ? currentNetworkList?.length > 1
    : false;

  const showCryptoTransfersSearchBar =
    (assetsListForDeposit?.length ?? 0) > 10 || !!assetFilterSearchValue;

  const hasAssetsForDeposit =
    assetsListForDeposit && assetsListForDeposit.length > 0;

  const renderMiddleScrollableRegion = () => {
    if (isAssetsListLoading) {
      return <AssetsListSkeleton />;
    }

    if (!isAssetsListLoading && !hasAssetsForDeposit) {
      return <NoResults searchTerm={assetFilterSearchValue} />;
    }

    return pageId === TransfersCommonStepsIds.ChooseAsset ? (
      <AssetPickerList
        onPick={bindAssetData}
        value={selectedAssetData?.id}
        variant={TransfersSectionVariants.Deposit}
      />
    ) : pageId === TransfersCommonStepsIds.ChooseNetwork &&
      currentNetworkList ? (
      <NetworkPickerList
        locale={locale}
        networkList={currentNetworkList}
        onNetworkSelected={bindNetworkData}
        state={state}
        translations={transfersTranslations}
        variant={TransfersSectionVariants.Deposit}
      />
    ) : pageId === TransfersCommonStepsIds.AssetAndNetworkSummary ? (
      <AssetAndNetworkPage
        assetList={assetsListForDeposit}
        state={state}
        variant={TransfersSectionVariants.Deposit}
        translations={transfersTranslations}
      />
    ) : pageId === TransfersDepositStepsIds.DepositWallet ? (
      <DepositWalletPage state={state} translations={transfersTranslations} />
    ) : null;
  };

  return (
    <TransfersLayout
      visual={
        TransfersDepositStepsIds.DepositWallet === pageId
          ? 'neutral'
          : 'default'
      }
      wrapperProps={{
        'aria-label': 'crypto transfers deposit flow',
      }}
      topRegion={
        <>
          <TransfersActionBar
            transfersTranslations={transfersTranslations}
            state={state}
            hasMultipleNetworks={hasMultipleNetworks}
          />

          {showCryptoTransfersSearchBar &&
          pageId === TransfersCommonStepsIds.ChooseAsset ? (
            <CryptoTransfersSearchBar
              placeholder={transfersTranslations.searchAssets}
            />
          ) : null}
        </>
      }
      middleScrollableRegion={renderMiddleScrollableRegion()}
    />
  );
};

export { DepositScreen };
